import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignQuote = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    fetchQuote();
    // eslint-disable-next-line
  }, [token]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/public/quote-signature/${token}`);
      setQuote(response.data.quote);
      setCompanyName(response.data.company_name);
      
      if (response.data.quote.status === 'accepted') {
        setSigned(true);
      }
    } catch (error) {
      toast.error('Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSigning(true);
    try {
      await axios.post(`${API}/public/quote-signature/${token}/accept`);
      toast.success('Devis signé électroniquement avec succès !');
      setSigned(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-600">Chargement...</div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <FileText className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-900 mb-2">Lien invalide</h2>
          <p className="text-stone-600">Ce lien de signature n'est plus valide ou a expiré.</p>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <Card className="max-w-2xl w-full p-8 sm:p-12 text-center bg-white shadow-lg">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-success" />
          </div>
          <h2 className="text-3xl font-bold text-stone-900 mb-4">Devis signé !</h2>
          <p className="text-stone-600 text-lg mb-6">
            Votre signature électronique a bien été enregistrée.
          </p>
          <div className="bg-stone-50 p-6 rounded-lg border border-stone-200 text-left">
            <p className="text-sm text-stone-700 mb-2">
              <strong>Devis n°:</strong> {quote.quote_number}
            </p>
            <p className="text-sm text-stone-700 mb-2">
              <strong>Montant TTC:</strong> {quote.total_ttc.toFixed(2)} €
            </p>
            {quote.signed_at && (
              <p className="text-sm text-stone-700">
                <strong>Signé le:</strong> {new Date(quote.signed_at).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
          <p className="text-stone-600 text-sm mt-6">
            Vous recevrez prochainement vos factures par email.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-stone-50 p-4">
      <div className="max-w-4xl w-full mx-auto my-8">
        <Card className="p-6 sm:p-8 bg-white shadow-lg">
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">
              Signature électronique du devis
            </h1>
            <p className="text-stone-600">
              Devis n° <strong>{quote.quote_number}</strong> - {companyName}
            </p>
          </div>

          <div className="bg-stone-50 p-6 rounded-lg border border-stone-200 mb-6">
            <h2 className="text-xl font-bold text-stone-900 mb-4">Récapitulatif</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-stone-700">Client:</span>
                <span className="font-medium text-stone-900">{quote.client_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-700">Email:</span>
                <span className="font-medium text-stone-900">{quote.client_email}</span>
              </div>
              <div className="border-t border-stone-200 pt-3">
                <div className="flex justify-between mb-2">
                  <span className="text-stone-700">Montant HT:</span>
                  <span className="font-mono text-stone-900">{quote.total_ht.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-stone-700">TVA ({quote.tva}%):</span>
                  <span className="font-mono text-stone-900">{(quote.total_ttc - quote.total_ht).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t border-stone-300 pt-3">
                  <span className="text-stone-900">Total TTC:</span>
                  <span className="text-primary font-mono">{quote.total_ttc.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-stone-50 p-6 rounded-lg border border-stone-200 mb-6">
            <h3 className="text-lg font-bold text-stone-900 mb-3">Description des travaux</h3>
            <p className="text-stone-700 whitespace-pre-wrap">{quote.content}</p>
          </div>

          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-6">
            <h3 className="text-lg font-bold text-stone-900 mb-3">Signature électronique</h3>
            <p className="text-sm text-stone-700 mb-4">
              En cliquant sur "Accepter et signer", vous acceptez ce devis et le signez électroniquement.
              Cette signature a la même valeur juridique qu'une signature manuscrite selon le règlement eIDAS.
            </p>
            <ul className="text-sm text-stone-600 space-y-1 ml-4">
              <li>• Votre adresse IP et la date seront enregistrées</li>
              <li>• Vous recevrez une confirmation par email</li>
              <li>• Les factures seront générées automatiquement</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleAccept}
              disabled={signing}
              className="flex-1 bg-primary text-white hover:bg-primary/90 py-6 text-lg font-medium shadow-md hover:shadow-lg transition-all"
            >
              {signing ? 'Signature en cours...' : 'Accepter et signer électroniquement'}
            </Button>
          </div>

          <p className="text-xs text-stone-500 text-center mt-6">
            Document sécurisé - Signature électronique conforme au règlement eIDAS
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SignQuote;
